// Central definitions for status enums.
//
// Each enum is declared once here as:
//   - a `*_STATUSES` tuple   → feeds `z.enum(...)` in forms and the DB schema's type
//   - a `*_STATUS_OPTIONS`    → feeds `EnumSelect` (value + human label)
//
// Keeping both in one place means a new status is added in exactly one spot and
// every form, filter, and create dialog picks it up.

export interface EnumOption {
  value: string;
  label: string;
}

// ── User roles ──
//
// Stored on Better Auth's `user.role`. `admin` manages users and counts as a PM;
// `ie` is the least access, so it is what an account gets if nothing says otherwise.
export const USER_ROLES = ["admin", "pm", "ie"] as const;
export type UserRole = (typeof USER_ROLES)[number];
export const USER_ROLE_OPTIONS: readonly { value: UserRole; label: string }[] = [
  { value: "pm", label: "PM" },
  { value: "ie", label: "IE" },
  { value: "admin", label: "Admin" },
];

// ── Photo portal (src/db/schema/) ──
export const ALBUM_GROUPS = ["MC checklist", "Weekly progress", "Other"] as const;
export type AlbumGroup = (typeof ALBUM_GROUPS)[number];
export const ALBUM_GROUP_OPTIONS: readonly EnumOption[] = ALBUM_GROUPS.map((value) => ({
  value,
  label: value,
}));
export const CAPTURE_SOURCES = ["exif", "upload", "device"] as const;
/** Where a photo made in this app came from: the phone field app, or the web upload. */
export const UPLOAD_CHANNELS = ["field", "web"] as const;
export type UploadChannel = (typeof UPLOAD_CHANNELS)[number];
/** Where a photo is in the phone's upload queue (`src/lib/upload-queue.ts`). Never stored on the server. */
export const UPLOAD_QUEUE_STATUSES = ["waiting", "sending", "sent", "failed", "duplicate"] as const;
/** Photo types a PM may upload (field app and web). */
// HEIC is refused for the POC: the production image (Linux) cannot decode it (docs/DECISIONS.md).
export const UPLOAD_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export const SHARE_EFFECTS = ["include", "exclude"] as const;
export type ShareEffect = (typeof SHARE_EFFECTS)[number];
export const SHARE_EFFECT_OPTIONS: readonly { value: ShareEffect; label: string }[] = [
  { value: "include", label: "Shared" },
  { value: "exclude", label: "Excluded" },
];
export const SHARE_SCOPES = ["project", "album", "photo"] as const;
export type ShareScope = (typeof SHARE_SCOPES)[number];
export const SHARE_SCOPE_OPTIONS: readonly { value: ShareScope; label: string }[] = [
  { value: "project", label: "Project" },
  { value: "album", label: "Album" },
  { value: "photo", label: "Photo" },
];
export const EXTRACTION_CONFIDENCES = ["high", "medium", "low"] as const;
export const EXTRACTION_STATUSES = ["suggested", "confirmed", "rejected"] as const;
export const EXTRACTION_RUN_OUTCOMES = ["stored", "no_reading", "bad_output", "failed"] as const;
export const ZIP_JOB_STATUSES = ["queued", "running", "done", "failed", "expired"] as const;

// --- S3_FEATURE_START ---
// ── File visibility (src/db/schema/files.ts) ──
//
// Storage is ALWAYS private. A `public` file is one the app chooses to serve at
// its stable `/f/:id/:name` URL (a redirect to a short-lived signed link) — the
// bucket itself never becomes public. `private` is the default for every file.
export const FILE_VISIBILITIES = ["private", "public"] as const;
export type FileVisibility = (typeof FILE_VISIBILITIES)[number];
// No `FILE_VISIBILITY_OPTIONS` yet: no screen offers the choice as a select.
// Add one here (value + label) the day one does.
// --- S3_FEATURE_END ---

// ── Audit log ──
//
// `action` is the `<entity>.<verb>` string stored on every audit_log row, and
// `entityType` is the coarse kind it groups under. Both are validated against
// these allowlists in `recordAudit` (src/lib/audit.ts) and drive the filter
// dropdowns in the admin viewer. Add a new action here when you audit a new
// mutation — the helper rejects anything not listed (see .claude/rules/audit.md).

export const AUDIT_ENTITY_TYPES = [
  // --- S3_FEATURE_START ---
  "file",
  // --- S3_FEATURE_END ---
  "user",
  "auth",
  "seed_pack",
  "import_run",
  "photo",
  "zip_job",
  "share",
  "checklist_item",
  "extraction",
  "ai_review_job",
  "checklist_suggestion",
  "photo_review",
] as const;
export type AuditEntityType = (typeof AUDIT_ENTITY_TYPES)[number];

export const AUDIT_ACTIONS = [
  // --- S3_FEATURE_START ---
  // A file made public or private again (`PATCH /api/files/:id/visibility`).
  "file.visibility_changed",
  // --- S3_FEATURE_END ---
  // Authentication events (captured via Better Auth hooks).
  "auth.login",
  "auth.login_failed",
  "auth.logout",
  "auth.signup",
  "auth.password_reset_requested",
  "auth.password_reset_completed",
  // Admin user management (Better Auth admin plugin endpoints).
  "user.created",
  "user.deleted",
  "user.role_changed",
  "user.updated",
  "user.banned",
  "user.unbanned",
  "user.password_reset",
  // `bun run db:seed` added rows from the committed seed pack (src/db/seed-pack.ts).
  "seed_pack.loaded",
  // `bun run import` loaded MESH 1 photos and metadata (scripts/import.ts).
  "import_run.completed",
  // `bun run backfill:source-keys` re-keyed photos to their exact on-disk spelling (one run-level row).
  "photo.source_keys_backfilled",
  // `bun run backfill:rendition-etags` stored the content ETag of existing renditions (one run-level row).
  "photo.rendition_etags_backfilled",
  // A rendition with no stored ETag was read and hashed on first request (one row per photo variant).
  "photo.rendition_etag_stored",
  // A photo was uploaded from the field app or the web, its original kept in app storage.
  "photo.uploaded",
  // A background download (zip) was asked for, finished, failed, handed back to the queue by a
  // stopping server, expired after its retention, or was downloaded.
  "zip_job.created",
  "zip_job.completed",
  "zip_job.failed",
  "zip_job.released",
  "zip_job.expired",
  "zip_job.downloaded",
  // A PM shared or excluded something with an IE firm, or withdrew that.
  "share.created",
  "share.revoked",
  // A PM linked a photo to a checklist item, unlinked it, or checked or unchecked the item.
  "checklist.photo_assigned",
  "checklist.photo_unassigned",
  "checklist.checked",
  "checklist.unchecked",
  // A PM confirmed, corrected or rejected a suggested nameplate reading.
  "extraction.confirmed",
  "extraction.rejected",
  // Photo AI: a PM asked for a check, confirmed or rejected a suggested checklist match, or
  // dismissed a quality flag.
  "ai_review.requested",
  "checklist_suggestion.confirmed",
  "checklist_suggestion.rejected",
  "photo_flag.dismissed",
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];
export const AUDIT_ACTION_OPTIONS: readonly EnumOption[] = AUDIT_ACTIONS.map((value) => ({
  value,
  label: value,
}));

/** Quality flags on a photo. `near_duplicate` comes from metadata; the rest from the AI. */
export const PHOTO_FLAGS = [
  "blurry",
  "too_dark",
  "subject_cut_off",
  "subject_blocked",
  "nameplate_unreadable",
  "wrong_album",
  "near_duplicate",
] as const;
export type PhotoFlag = (typeof PHOTO_FLAGS)[number];
export const PHOTO_FLAG_LABELS: Record<PhotoFlag, string> = {
  blurry: "Blurry",
  too_dark: "Too dark",
  subject_cut_off: "Subject cut off",
  subject_blocked: "Subject blocked",
  nameplate_unreadable: "Nameplate unreadable",
  wrong_album: "May not belong in this album",
  near_duplicate: "Near-duplicate",
};

export const PHOTO_CONDITION_TAGS = [
  "damage",
  "corrosion",
  "open_enclosure",
  "missing_label",
  "loose_wiring",
  "debris",
  "water",
  "vegetation",
] as const;
export type PhotoConditionTag = (typeof PHOTO_CONDITION_TAGS)[number];
export const PHOTO_CONDITION_TAG_LABELS: Record<PhotoConditionTag, string> = {
  damage: "Damage",
  corrosion: "Corrosion",
  open_enclosure: "Open enclosure",
  missing_label: "Missing label",
  loose_wiring: "Loose wiring",
  debris: "Debris",
  water: "Water",
  vegetation: "Vegetation",
};

export const CHECKLIST_SUGGESTION_STATUSES = ["suggested", "confirmed", "rejected"] as const;
export const AI_REVIEW_JOB_STATUSES = ["queued", "running", "done", "failed"] as const;
export const AI_REVIEW_SCOPES = ["album", "photo"] as const;
